import * as React from 'react';
import { cloneDeep, debounce, throttle } from 'lodash';
import { DeleteOutlined } from '@ant-design/icons';
import { ConfirmationMessageType, SourceType } from '@config/index';
import {
  BodyGroup,
  NiSpin,
  AddButton,
  PromptListComp,
  NiModal,
  NiForm,
  NiBooleanRadio,
  NiTable,
  NiSelect,
  NiRadio,
  NiInput,
  ReferenceTextArea,
  HelperPopover,
  sourceRender,
  ExprMode
} from '@component/index';
import {
  TrainingPhraseOwnerType,
  TrainingPhraseConfig,
  ParameterConfig,
  TrainingPhraseType,
  Attribute,
  PromptInfo,
  PromptType,
  PromptOwnerType,
  confirmationInfoToServer,
  ConfirmationInfo,
  FrameItem,
  DataQuery,
  TriggerCondition,
  ParameterType,
  FramelyAction,
  ListAction,
  ActionType,
  IntentInfo
} from '@interface/index';
import {
  isEqual,
  ParameterMapStore,
  getAgentId,
  TriggerConditionsRender,
  isStructAgent
} from '@lib/index';
import { localizedString } from '@locale/index';
import { Switch, Popover, Popconfirm, Space } from 'antd';
import { TrainingPhraseComp } from '@component/training-phrases';
import { ConditionTable } from '@component/trigger-condition/condition-table';
import { AnnotationOperator } from '@page/action-parameter/component/annotation-operator';

import '@style/confirmation-group/index.less';
import { ActionList } from '@page/intent/component/action-list';
import { NewPromptList, PromptListHandle } from '@component/prompt/new-prompt-list';
import CodeTextArea from '@component/code-textarea/code-textarea';
import EmptyLine from '@component/business-component/empty-line/empty-line';


const defaultData: ConfirmationInfo = {
  id: '',
  agentId: '',
  ownerId: '',
  prompts: [],
  explicitConfirmation: false,
  messageType: ConfirmationMessageType.SingleValue,
  conditionType: SourceType.CODE_EXPRESSION,
  codeCondition: '',
  affirmatives: [],
  negatives: []
};

export type ConfirmationAction = {
  action: 'add' | 'update' | 'delete';
  item: ConfirmationInfo;
  nextData: ConfirmationInfo[];
};


interface ConfirmationGroupProps {
  multiValued?: boolean;
  disabled?: boolean;
  frame: FrameItem;
  attribute?: Attribute;
  data: ConfirmationInfo[];
  selectedParameters: Attribute[];
  dataQueries: DataQuery[];
  availableIntents?: IntentInfo[];
  showSwitch?: boolean;
  switchChecked?: boolean;
  onSwitchChange?: (checked: boolean) => void;
  onChange: (action: ConfirmationAction) => void;
  cardDeleteClick?: () => void;
}

interface ConfirmationGroupState {
  modalVisible: boolean;
  modalIndex: number;
  modalData: ConfirmationInfo;
  loading: boolean;
}

export class ConfirmationGroup extends React.Component<
  ConfirmationGroupProps,
  ConfirmationGroupState
> {

  constructor(props: ConfirmationGroupProps) {
    super(props);

    this.state = {
      modalVisible: false,
      modalIndex: -1,
      modalData: defaultData,
      loading: false
    };
  }

  componentDidMount() {
    // this.onInit();
  }

  componentDidUpdate(prevProps: any) {
    if (!isEqual(prevProps, this.props)) {
      // this.onInit();
    }
  }

  render() {
    const { disabled, data, frame, attribute, multiValued, showSwitch, switchChecked, onSwitchChange } = this.props;
    const { loading, modalData } = this.state;

    return <NiSpin spinning={loading}>
      {!attribute
        ? <BodyGroup
          foldable
          defaultFolded
          className="confirmation-group__layout"
          title={localizedString('annotation_confirmation')}
          titleExtra={<HelperPopover localizedKey="frame_hint_confirmations" />}
          extra={data && data.filter(item => !item.toBeDeleted).length && <AddButton
            disabled={disabled || !!frame.originKey}
            onClick={this.handleAddConfirmation}
          /> || ''}
        >
          {this.listComp()}
          {this.modalComp()}
        </BodyGroup>
        : <BodyGroup
          noPadding
          foldable
          defaultFolded
          // className={`confirmation-group__layout slot-confirmation-card ${!switchChecked && 'no-content'}`}
          title={attribute.multiValue ? (multiValued ? 'List level user confirmation' : 'Item level user confirmation') : 'User confirmation'}
          titleExtra={<Space size={12}><HelperPopover localizedKey={multiValued ? 'multi_valued_confirmation_popover' : 'confirmation_popover'} />
            {showSwitch && <Switch disabled={disabled ||
              !!frame.originKey} checked={switchChecked} onChange={this.props.onSwitchChange} />}
          </Space>}
          extra={[
            // switchChecked &&
            data && data.filter(item => !item.toBeDeleted).length && <AddButton
              disabled={disabled}
              onClick={() => {
                this.handleAddConfirmation();
              }}
            /> || ''
            // ,
            // <AnnotationOperator
            //   disabled={disabled ||  !!frame.originKey}
            //   onDelete={() => {
            //     if (this.props.cardDeleteClick) {
            //       this.props.cardDeleteClick();
            //     }
            //   }}
            // />
          ]
          }
        >
          {this.listComp()}
          {this.modalComp()}
        </BodyGroup>
      }
    </NiSpin >;
  }

  listComp = () => {

    const { disabled, data, onChange, frame } = this.props;

    return data && data.filter(item => !item.toBeDeleted).length ? <NiTable
      className="confirmation-list"
      pagination={false}
      dataSource={data}
      rowKey={(record: ConfirmationInfo) => record.id || 'new'}
      onRow={(record: ConfirmationInfo, index?: number) => {
        return {
          index,
          onClick: () => {
            this.setState({
              modalIndex: index || 0,
              modalData: record,
              modalVisible: true
            });
          }
        };
      }}
      columns={[{
        title: localizedString('common_condition'),
        dataIndex: 'triggerConditions',
        // width: '85%',
        render: (tcs: TriggerCondition[], record: ConfirmationInfo) => {

          return <div className="content-wrap">
            {record.conditionType === SourceType.REGULAR
              ? ((tcs && tcs.length > 0)
                ? <TriggerConditionsRender data={tcs} />
                : <span className="condition-undefined">Condition undefined</span>
              ) : (record.codeCondition.length
                ? <span>{record.codeCondition}</span>
                : <span className="condition-undefined">Condition undefined</span>
              )
            }
          </div>;
        }
      }, {
        width: 150,
        title: 'Source',
        dataIndex: 'inheritedFromCompositeName',
        key: 'source',
        render: (sourceName: string, record: ConfirmationInfo) => {
          return sourceRender(record, frame.type);
        }
      }, {
        title: '',
        dataIndex: 'operation',
        width: 70,
        render: (_: any, record: ConfirmationInfo, index: number) => {
          return <div className="badge-wrap">
            <div
              className="delete-area"
              style={{
                width: 30
              }}
            >
              {!disabled &&
                !record.originKey &&
                !record.inheritedFromCompositeId &&
                <DeleteOutlined
                  className="delete-icon"
                  key={`del-confirmation-${record.id}`}
                  style={{
                    cursor: 'pointer'
                  }}
                  onClick={async (e: React.MouseEvent<any>) => {
                    e.stopPropagation();

                    const nextData = cloneDeep(data);
                    const [item] = nextData.splice(index, 1, {
                      ...data[index],
                      toBeDeleted: true
                    });
                    onChange({
                      item,
                      nextData,
                      action: 'delete'
                    });
                  }}
                />
              }
            </div>
          </div>;
        }
      }]}
    /> : <EmptyLine text="Click to add a confirmation" onClick={this.handleAddConfirmation} />;
  }

  promptRef = React.createRef<PromptListHandle>();

  modalComp = () => {

    const { frame } = this.props;
    const {
      attribute,
      disabled,
      selectedParameters,
      dataQueries,
      availableIntents,
      multiValued
    } = this.props;
    const { modalVisible, modalIndex, modalData } = this.state;

    return <NiModal
      title={<span>{localizedString(multiValued ? 'annotation_multi_valued_confirmation' : 'annotation_confirmation')}
        <HelperPopover localizedKey="confirmation_popover" />
      </span>}
      width={'60%'}
      open={modalVisible}
      okText={(modalData.inheritedFromCompositeId) &&
        <Popconfirm
          title={
            <span>{localizedString('confirmation_inherit_copy_popconfirm')}
              <HelperPopover localizedKey="confirmation_inherit_copy_popover" />
            </span>
          }
          onConfirm={this.handleModalConfirm}
        >
          <a href="#">{localizedString('common_confirm')}</a>
        </Popconfirm> || undefined
      }
      onOk={disabled ||
        modalData.inheritedFromCompositeId
        ? undefined : () => {
          this.handleModalConfirm();
        }}
      okButtonProps={disabled
        ? {
          type: 'ghost',
          disabled: true
        } : undefined}
      onCancel={() => {
        this.setState({ modalVisible: false });
      }}
    >
      <NiForm
        titleline
        direction="vertical"
        items={[{
          key: 'condition-type',
          label: localizedString('common_condition'),
          labelExtra: modalData.conditionType === SourceType.REGULAR && <NiRadio
            disabled={disabled}
            value={modalData.conditionType}
            onChange={(e: any) => {
              const next = cloneDeep(modalData);
              next.conditionType = e.target.value;
              this.setState({ modalData: next });
            }}
            keyValue={[
              { key: SourceType.REGULAR, value: localizedString('condition_type_regular') },
              { key: SourceType.CODE_EXPRESSION, value: localizedString('condition_type_code_expression') }
            ]}
          />,
          content: modalData.conditionType === SourceType.REGULAR
            ? <ConditionTable
              style={{ flex: 1 }}
              data={modalData.triggerConditions || []}
              frame={frame}
              attribute={attribute}
              disabled={disabled || !!modalData.originKey}
              selectedParameters={selectedParameters}
              dataQueries={dataQueries}
              onChange={(next: TriggerCondition[]) => {
                const nextData = cloneDeep(modalData);
                nextData.triggerConditions = next;
                this.setState({ modalData: nextData });
              }}
            />
            : <CodeTextArea
              inModal
              dataId="source"
              disabled={disabled}
              language="kotlin"
              data={modalData.codeCondition}
              update={(value: any) => {
                const nextData = cloneDeep(modalData);
                nextData.codeCondition = value;
                this.setState({ modalData: nextData });
              }}
              completionContext={
                {
                  rawCodeType: frame
                }
              }
            />
          // <ReferenceTextArea
          //     style={{ flex: 1 }}
          //     parameterMap={ParameterMapStore.all()}
          //     initParameters={selectedParameters}
          //     rawCodeMode={true}
          //     rawCodeType={frame}
          //     rawCodeExprMode={ExprMode.condition}
          //     disabled={disabled}
          //     data={modalData.codeCondition}
          //     placeholder={localizedString('code_condition_placeholder')}
          //     update={(value: any) => {
          //       const nextData = cloneDeep(modalData);
          //       nextData.codeCondition = value;
          //       this.setState({ modalData: nextData });
          //     }}
          //     comment={modalData.codeComment}
          //     onCommentChange={(value: any) => {
          //       const nextData = cloneDeep(modalData);
          //       nextData.codeComment = value;
          //       this.setState({ modalData: nextData });
          //     }}
          //   />
        },
        {
          key: 'attribute-confirm',
          label: localizedString('attribute_confirm'),
          labelExtra: <Space direction="horizontal">
            {/* {
              <NiSelect
                value={modalData.messageType}
                disabled={disabled || !!modalData.originKey}
                onChange={(value: string) => {
                  const nextData = cloneDeep(modalData);
                  nextData.messageType = value as ConfirmationMessageType;

                  if (value === ConfirmationMessageType.MultipleValue) {
                    nextData.listAction = {
                      type: 'list_action',
                      sourceType: SourceType.CODE_EXPRESSION,
                      source: '',
                      variable: [],
                      parameters: [],
                      header: '',
                      footer: '',
                      body: '',
                      templateId: '',
                      itemSize: 5,
                      delimiterField: '\\n'
                    };
                  }

                  this.setState({ modalData: nextData });
                }}
              >
                <NiSelect.Option
                  value={ConfirmationMessageType.SingleValue}
                  key={ConfirmationMessageType.SingleValue}
                >
                  {localizedString('confirmation_message_type_single_value')}
                </NiSelect.Option>
                <NiSelect.Option
                  value={ConfirmationMessageType.MultipleValue}
                  key={ConfirmationMessageType.MultipleValue}
                >
                  {localizedString('confirmation_message_type_multi_value')}
                </NiSelect.Option>
              </NiSelect>
            } */}
            {
              !multiValued && <NiBooleanRadio
                disabled={disabled}
                value={modalData.explicitConfirmation}
                onChange={(value: boolean) => {
                  const nextData = cloneDeep(modalData);
                  nextData.explicitConfirmation = value;
                  this.setState({ modalData: nextData });
                }}
                falseWord={localizedString('value_rec_implicit_confirmation')}
                trueWord={localizedString('value_rec_explicit_confirmation')}
                style={{ marginTop: 4 }}
              /> || ''
            }
          </Space>,
          extra: modalData.messageType !== ConfirmationMessageType.MultipleValue && modalData.prompts && modalData.prompts.filter(item => !item.toBeDeleted).length ? <AddButton
            disabled={disabled || isStructAgent()}
            onClick={(e: any) => {
              this.promptRef.current?.add(e);
            }} /> : '',
          content:
            <div style={{ flex: 1 }}>
              {
                modalData.messageType === ConfirmationMessageType.MultipleValue
                  ? (modalData.listAction && <ActionList
                    inModal
                    singleAction
                    singleChannel
                    hideNumberOfEntries
                    hideSource={!!attribute}
                    title={localizedString('list_reply_display')}
                    frame={frame}
                    selectedParameters={selectedParameters}
                    globalSelectedParameters={[]}
                    availableIntents={availableIntents || []}
                    dataQueries={dataQueries}
                    disabled={disabled}
                    actionsTypes={[ActionType.LIST]}
                    data={modalData.listAction && [modalData.listAction as FramelyAction] || []}
                    onChange={(next: FramelyAction[]) => {
                      const nextData = cloneDeep(modalData);
                      nextData.listAction = next[0] as ListAction;
                      this.setState({ modalData: nextData });
                    }}
                  /> || '')
                  : <NewPromptList
                    inModal
                    ref={this.promptRef}
                    style={{ flex: 1 }}
                    agentId={getAgentId()}
                    frameId={frame.id || ''}
                    ownerId={modalData.id || ''}
                    ownerType={PromptOwnerType.confirm}
                    promptType={PromptType.confirm}
                    disableChange={disabled || isStructAgent()}
                    // placeholderFrist={localizedString('response_confirm_question_placeholder')}
                    isSingleLine={false}
                    allowClearAll={true}
                    data={modalData.prompts || []}
                    parameterMap={ParameterMapStore.all()}
                    parameters={selectedParameters}
                    update={(values: PromptInfo[]) => {
                      const next = cloneDeep(modalData);
                      next.prompts = values;
                      this.setState({ modalData: next });
                    }}
                  />
              }

              {/* {modalData.explicitConfirmation && !multiValued &&
                <NiForm
                  style={{ flex: 1, marginTop: 16 }}
                  direction="vertical"
                  items={[{
                    key: 'attribute-confirm-yes',
                    label: <span style={{ marginLeft: 12 }}>{localizedString('attribute_expression_yes')}</span>,
                    content: <TrainingPhraseComp
                      style={{ flex: 1 }}
                      disableChange={disabled || isStructAgent()}
                      agentId={getAgentId()}
                      frameId={frame.id || ''}
                      ownerId={modalData.id || ''}
                      ownerType={TrainingPhraseOwnerType.CONFIRMATION}
                      phraseType={TrainingPhraseType.CONFIRM_YES}
                      isSingleLine={false}
                      allowClearAll={true}
                      data={modalData.affirmatives || []}
                      parameterMap={ParameterMapStore.all()}
                      parameters={selectedParameters}
                      update={(values: TrainingPhraseConfig[]) => {
                        const next = cloneDeep(modalData);
                        next.affirmatives = values;
                        this.setState({ modalData: next });
                      }}
                    />
                  }, {
                    key: 'attribute-confirm-no',
                    label: <span style={{ marginLeft: 12 }}>{localizedString('attribute_expression_no')}</span>,
                    content: <TrainingPhraseComp
                      style={{ flex: 1 }}
                      disableChange={disabled || isStructAgent()}
                      agentId={getAgentId()}
                      frameId={frame.id || ''}
                      ownerId={modalData.id || ''}
                      ownerType={TrainingPhraseOwnerType.CONFIRMATION}
                      phraseType={TrainingPhraseType.CONFIRM_NO}
                      isSingleLine={false}
                      allowClearAll={true}
                      data={modalData.negatives || []}
                      parameterMap={ParameterMapStore.all()}
                      parameters={selectedParameters}
                      update={(values: TrainingPhraseConfig[]) => {
                        const next = cloneDeep(modalData);
                        next.negatives = values;
                        this.setState({ modalData: next });
                      }}
                    />
                  }]}
                />
              } */}
            </div>
        }]}
      />
    </NiModal>;
  }

  handleModalConfirm = debounce(() => {
    const { data, onChange } = this.props;

    const { modalData, modalIndex } = this.state;
    const nextData = cloneDeep(data || []);
    if (modalIndex === -1) {
      nextData.push(modalData);
      onChange({
        nextData,
        action: 'add',
        item: modalData
      });
    } else {
      nextData[modalIndex] = modalData;
      onChange({
        nextData,
        action: 'update',
        item: modalData
      });
    }

    this.setState({ modalVisible: false });
  }, 500);

  handleAddConfirmation = () => {
    const { frame, attribute, multiValued } = this.props;
    this.setState({
      modalIndex: -1,
      modalData: {
        ...defaultData,
        multiValued,
        ownerId: !!attribute ? attribute.id : frame.id,
        ownerType: !!attribute ? ParameterType.ATTRIBUTE : frame.type,
        frameId: !!attribute ? frame.id : '',
        messageType: multiValued ? ConfirmationMessageType.MultipleValue : ConfirmationMessageType.SingleValue,
        listAction: multiValued && {
          type: 'list_action',
          sourceType: SourceType.CODE_EXPRESSION,
          source: '',
          variable: [],
          parameters: [],
          header: '',
          footer: '',
          body: '',
          templateId: '',
          itemSize: 5,
          delimiterField: '\\n'
        } || undefined,

      },
      modalVisible: true
    });
  }
}
